import type { CharacterProfile, ChatExportFields, WorldDocument } from "@shared/schema";
import type { APIKeyConfig, ValidationResult } from "@/types/api-key";
import {
  BaseProvider,
  type ProviderResponse,
  type ImagePlanPrompts,
  type TextGenerationOptions,
  type ImageGenerationOptions,
  type RoleplayOptions,
  type EditOptions,
} from "./provider-interface";
import { parseGeminiUsage } from "./usage-helpers";
import { parseJsonLike } from "./json-utils";

/**
 * Gemini AI Provider Implementation
 *
 * Supports:
 * - Text generation (all capabilities)
 * - Image generation (gemini-2.5-flash-image, imagen-4.0-*)
 * - Image editing with reference
 * - Native PDF document support
 */
export class GeminiProvider extends BaseProvider {
  // Constants
  private static readonly SAFETY_SETTINGS = [
    { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
    { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
    { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
    { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' },
  ];

  private static readonly IMAGE_MAX_ATTEMPTS = 2;
  private static readonly IMAGE_RETRY_DELAY_MS = 1200;
  private static readonly IMAGE_RETRYABLE_FINISH_REASONS = new Set(['IMAGE_OTHER']);

  // Validation
  async validateApiKey(config: {
    apiKey: string;
    model?: string;
  }): Promise<ValidationResult> {
    try {
      const model = config.model || 'gemini-2.5-flash';
      const json = await this.postGenerateContent(model, config.apiKey, [
        { role: 'user', parts: [{ text: 'Return the string OK.' }] },
      ]);
      this.extractTextFromCandidates(json);
      return { isValid: true };
    } catch (e: any) {
      return { isValid: false, error: String(e?.message || e) };
    }
  }

  // Character Profile Generation
  async generateCharacterProfile(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    worldDocument?: WorldDocument,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<CharacterProfile>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const fullPrompt = `${prompts.system}\n\n${prompts.user}`;
    let contents: any[];

    if (worldDocument?.rawData && worldDocument.filename.toLowerCase().endsWith('.pdf')) {
      contents = [
        {
          role: 'user',
          parts: [
            {
              text: `${prompts.system}\n\nWORLD DOCUMENT: Attached PDF ${worldDocument.filename}. Use its content as context.\n\n${prompts.user}`,
            },
            { inlineData: { data: worldDocument.rawData, mimeType: 'application/pdf' } },
          ],
        },
      ];
    } else {
      contents = [{ role: 'user', parts: [{ text: fullPrompt }] }];
    }

    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    const text = this.extractTextFromCandidates(json);
    const parsed = parseJsonLike<any>(text);

    // Accept either direct CharacterProfile or nested fields
    const data = parsed?.character_profile || parsed?.character || parsed;
    return { value: data as CharacterProfile, usage };
  }

  // Chat Export Fields
  async generateChatExportFields(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ChatExportFields>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const contents = [{ role: 'user', parts: [{ text: `${prompts.system}\n\n${prompts.user}` }] }];
    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    const text = this.extractTextFromCandidates(json);
    return { value: parseJsonLike<ChatExportFields>(text), usage };
  }

  // Image Plan Prompts
  async generateImagePlanPrompts(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ImagePlanPrompts>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const contents = [{ role: 'user', parts: [{ text: `${prompts.system}\n\n${prompts.user}` }] }];
    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    const text = this.extractTextFromCandidates(json);
    const value = parseJsonLike<ImagePlanPrompts>(text);
    return { value, usage };
  }

  // Field Rewrite
  async rewriteCharacterField(
    instructions: string,
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<string>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const contents = [{ role: 'user', parts: [{ text: instructions }] }];
    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    const text = this.extractTextFromCandidates(json).trim();

    // Strip code block markers if present
    const codeBlock = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    const value = (codeBlock ? codeBlock[1] : text).trim();
    return { value, usage };
  }

  // AI Edit
  async editCharacter(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: EditOptions
  ): Promise<ProviderResponse<any>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const contents = [{ role: 'user', parts: [{ text: `${prompts.system}\n\n${prompts.user}` }] }];
    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    const text = this.extractTextFromCandidates(json).trim();
    const parsed = parseJsonLike<any>(text);
    return { value: parsed, usage };
  }

  // Roleplay Reply
  async generateRoleplayReply(
    params: {
      system: string;
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    },
    config: APIKeyConfig,
    opts?: RoleplayOptions
  ): Promise<ProviderResponse<string>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const transcript = params.messages
      .map((m) => `${m.role === 'assistant' ? 'Character' : 'User'}: ${m.content}`)
      .join('\n');

    const finalPrompt = [
      params.system,
      'Conversation so far:',
      transcript,
      '',
      'Continue with the next message from Character only. Do not include explanations.',
    ].join('\n');

    const contents = [{ role: 'user', parts: [{ text: finalPrompt }] }];
    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    const text = this.extractTextFromCandidates(json).trim();
    return { value: text, usage };
  }

  // Image Generation
  async generateImage(
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    // Route Imagen 4.0 models to dedicated predict endpoint
    if (/^imagen-4\.0-/.test(config.model)) {
      return this.generateImagenImage(prompt, config, opts);
    }

    // Use gemini-2.5-flash-image for standard image generation
    const contents = [{ role: 'user', parts: [{ text: prompt }] }];

    for (let attemptIndex = 1; attemptIndex <= GeminiProvider.IMAGE_MAX_ATTEMPTS; attemptIndex += 1) {
      this.checkAborted(opts?.signal);

      const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
        signal: opts?.signal,
      });
      const usage = parseGeminiUsage(json);
      const candidates = json?.candidates || [];
      this.maybeThrowGeminiSafety(json);

      // Try to extract image data from candidates
      const imageData = this.extractImageData(json);
      if (imageData) {
        return { value: { imageData }, usage };
      }

      const finishReason = this.getImageFinishReason(json);
      const isRetryable = finishReason
        ? GeminiProvider.IMAGE_RETRYABLE_FINISH_REASONS.has(finishReason)
        : false;

      if (isRetryable && attemptIndex < GeminiProvider.IMAGE_MAX_ATTEMPTS) {
        await this.delayWithAbort(GeminiProvider.IMAGE_RETRY_DELAY_MS, opts?.signal);
        continue;
      }

      throw new Error(this.buildNoImageMessage(finishReason));
    }

    throw new Error('Gemini did not return image data.');
  }

  // Image Edit
  async generateImageEdit(
    referenceImage: string,
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const { mimeType, base64 } = this.splitDataUri(referenceImage);
    const contents = [
      {
        role: 'user',
        parts: [
          { inlineData: { data: base64, mimeType } },
          { text: prompt },
        ],
      },
    ];

    const json = await this.postGenerateContent(config.model, config.apiKey, contents, {
      signal: opts?.signal,
    });
    const usage = parseGeminiUsage(json);
    this.maybeThrowGeminiSafety(json);

    const imageData = this.extractImageData(json);
    if (imageData) {
      return { value: { imageData }, usage };
    }

    throw new Error('No image data returned by model');
  }

  // Private helper methods

  private async postGenerateContent(
    model: string,
    apiKey: string,
    contents: any,
    opts?: { signal?: AbortSignal }
  ): Promise<any> {
    const isImagePreviewModel = /image-preview/i.test(model);
    const resp = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': apiKey,
        },
        body: JSON.stringify({ contents, safetySettings: GeminiProvider.SAFETY_SETTINGS }),
        signal: opts?.signal,
      }
    );

    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || msg;
        const serialized = JSON.stringify(err || {});
        const details = Array.isArray(err?.error?.details) ? err.error.details : [];
        const detailReasons = (details || []).map((d: any) => d?.reason || d?.metadata?.reason || '').join(',');
        const text = `${msg} ${serialized} ${detailReasons}`;

        if (/PROHIBITED_CONTENT/i.test(text) || /\bsafety\b/i.test(text) || /blocked by safety/i.test(text)) {
          throw new Error(
            'Blocked by Google safety filters (PROHIBITED_CONTENT). Please adjust your prompt (avoid explicit sexual content, minors, or graphic violence) and try again.'
          );
        }

        if (isImagePreviewModel && (/internal error encountered/i.test(String(msg)) || resp.status >= 500)) {
          const helpful =
            'Gemini image generation returned an internal error. This often means your API key or project is not enabled for image output. Try one of the following:\n' +
            '- Use OpenRouter with the model "google/gemini-2.5-flash-image" in Settings.\n' +
            '- Or switch provider to OpenAI and use DALL-E 3 for images.\n' +
            '- If you intend to use Gemini directly, ensure your Google AI Studio key supports image generation for model gemini-2.5-flash-image.';
          throw new Error(helpful);
        }

        if (/permission/i.test(serialized) || /not authorized/i.test(serialized) || /api key/i.test(serialized)) {
          throw new Error(
            'Gemini API rejected the request. Verify your API key is valid and has access to the selected model.'
          );
        }
      } catch (e: any) {
        if (e instanceof Error && /Blocked by Google safety filters/.test(e.message)) {
          throw e;
        }
      }
      throw new Error(msg);
    }

    return resp.json();
  }

  private async postImagenPredict(
    model: string,
    apiKey: string,
    prompt: string,
    opts?: { signal?: AbortSignal }
  ): Promise<any> {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:predict`;
    const body = {
      instances: [{ prompt }],
      parameters: {
        numberOfImages: 1,
        aspectRatio: '1:1',
        imageSize: '2K',
        personGeneration: 'allow_adult',
      },
    };

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': apiKey,
      },
      body: JSON.stringify(body),
      signal: opts?.signal,
    });

    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || msg;
        const serialized = JSON.stringify(err || {});

        if (/PROHIBITED_CONTENT/i.test(serialized) || /safety/i.test(serialized)) {
          throw new Error(
            'Blocked by Google safety filters (PROHIBITED_CONTENT). Please adjust your prompt and try again.'
          );
        }

        if (/permission|not authorized|api key/i.test(serialized)) {
          throw new Error(
            'Gemini/Imagen API rejected the request. Verify your API key is valid and has access to Imagen 4.0.'
          );
        }
      } catch (e: any) {
        if (e instanceof Error && /Blocked by Google safety filters/.test(e.message)) {
          throw e;
        }
      }
      throw new Error(msg);
    }

    return resp.json();
  }

  private async generateImagenImage(
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>> {
    const json = await this.postImagenPredict(config.model, config.apiKey, prompt, {
      signal: opts?.signal,
    });

    const genImage = json?.generatedImages?.[0]?.image;
    const pred0 = json?.predictions?.[0];
    const imageBytes: string | undefined = genImage?.imageBytes || pred0?.bytesBase64Encoded;
    const mime: string = genImage?.mimeType || pred0?.mimeType || 'image/png';

    if (typeof imageBytes === 'string' && imageBytes.length > 0) {
      const imageData = `data:${mime};base64,${imageBytes}`;
      return {
        value: { imageData },
        usage: { inputTokens: 0, outputTokens: 0, totalTokens: 0 } as any,
      };
    }

    throw new Error('No image data returned by Imagen');
  }

  private extractTextFromCandidates(json: any): string {
    const candidates = json?.candidates || [];
    const parts = candidates.flatMap((c: any) => c?.content?.parts || []);

    // Prefer text
    const textPart = parts.find((p: any) => typeof p?.text === 'string');
    if (textPart?.text) return String(textPart.text);

    // Fallback to inlineData text-like outputs
    if (candidates[0]?.content?.parts?.length) {
      const firstText = candidates[0].content.parts.find((p: any) => typeof p?.text === 'string');
      if (firstText?.text) return String(firstText.text);
    }

    // Last resort: stringify
    return JSON.stringify(json);
  }

  private extractImageData(json: any): string | null {
    const candidates = json?.candidates || [];

    // Check candidates for inline image data
    for (const c of candidates) {
      const parts = c?.content?.parts || [];
      for (const p of parts) {
        if (p?.inlineData?.data && p?.inlineData?.mimeType) {
          const mime = String(p.inlineData.mimeType);
          const data = String(p.inlineData.data);
          return `data:${mime};base64,${data}`;
        }
        if (typeof p?.text === 'string') {
          const m = p.text.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
          if (m) return m[0];
        }
      }
    }

    // Check text output
    const text = this.extractTextFromCandidates(json);
    const m = text.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
    if (m) return m[0];

    // Check full JSON
    try {
      const whole = JSON.stringify(json);
      if (/PROHIBITED_CONTENT/i.test(whole) || /blocked by safety/i.test(whole)) {
        throw new Error(
          'Blocked by Google safety filters (PROHIBITED_CONTENT). Please adjust your prompt (avoid explicit sexual content, minors, or graphic violence) and try again.'
        );
      }

      const uriMatch = whole.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
      if (uriMatch) return uriMatch[0];

      const inlineDataMatch = whole.match(
        /"inlineData"\s*:\s*\{\s*"mimeType"\s*:\s*"([^"]+)"\s*,\s*"data"\s*:\s*"([A-Za-z0-9+/=]+)"/
      );
      if (inlineDataMatch && inlineDataMatch[1] && inlineDataMatch[2]) {
        return `data:${inlineDataMatch[1]};base64,${inlineDataMatch[2]}`;
      }
    } catch {}

    return null;
  }

  private splitDataUri(dataUri: string): { mimeType: string; base64: string } {
    const trimmed = String(dataUri || '').trim();
    const m = trimmed.match(/^data:([^;]+);base64,(.+)$/i);
    if (m && m[1] && m[2]) {
      return { mimeType: m[1], base64: m[2] };
    }
    // Fallback: assume PNG
    const cleaned = trimmed.replace(/^data:image\/[^;]+;base64,/i, '');
    return { mimeType: 'image/png', base64: cleaned };
  }

  private getImageFinishReason(json: any): string | undefined {
    const candidate = (json?.candidates || [])[0] || {};
    const finish = candidate?.finishReason || candidate?.finish_reason;
    return finish ? String(finish).toUpperCase() : undefined;
  }

  private buildNoImageMessage(finishReason?: string): string {
    if (finishReason) {
      return `Gemini did not return image data (finishReason: ${finishReason}). Try simplifying the description or retrying in a moment. If the issue persists, switch to an alternate image provider.`;
    }
    return 'Gemini did not return image data. Try simplifying the description, then retry or switch to another provider.';
  }

  private maybeThrowGeminiSafety(json: any): void {
    if (!json) return;

    const candidate = (json?.candidates || [])[0] || {};
    const promptFeedback = json?.promptFeedback || json?.prompt_feedback || {};
    const finish = candidate?.finishReason || candidate?.finish_reason;
    const blockReason = promptFeedback?.blockReason || promptFeedback?.block_reason;
    const reasons = [finish, blockReason].filter(Boolean).map((r) => String(r).toUpperCase());

    const safetyRatingsCandidate = candidate?.safetyRatings || candidate?.safety_ratings;
    const safetyRatingsFeedback = promptFeedback?.safetyRatings || promptFeedback?.safety_ratings;
    const safetyRatings = Array.isArray(safetyRatingsCandidate)
      ? safetyRatingsCandidate
      : Array.isArray(safetyRatingsFeedback)
      ? safetyRatingsFeedback
      : [];

    const flaggedRatings = safetyRatings.filter((rating: any) => {
      if (rating?.blocked === true) return true;
      const probability = String(rating?.probability || '').toUpperCase();
      return ['VERY_LIKELY', 'HIGH'].includes(probability);
    });

    const serialized = JSON.stringify(json || {});
    const textIndicatesBlock =
      /PROHIBITED_CONTENT/i.test(serialized) || /blocked by safety/i.test(serialized);

    const reasonTriggers = reasons.some((reason) =>
      [
        'IMAGE_SAFETY',
        'PROHIBITED_CONTENT',
        'SAFETY',
        'BLOCKLIST',
        'BLOCK_REASON_UNSPECIFIED',
        'OTHER',
      ].includes(reason)
    );

    if (!reasonTriggers && !flaggedRatings.length && !textIndicatesBlock) {
      return;
    }

    const reasonText = this.describeBlockReason(reasons[0]);
    const formattedCategories = flaggedRatings
      .map((rating: any) => this.formatSafetyCategory(rating?.category))
      .filter(Boolean);
    const categoryText = formattedCategories.length
      ? `Gemini flagged these categories: ${[...new Set(formattedCategories)].join(', ')}.`
      : '';
    const guidance =
      'Please adjust your prompt to remove policy-sensitive content (avoid explicit sexual content, minors, or graphic violence) and try again.';

    const message = [reasonText, categoryText, guidance].filter(Boolean).join(' ');
    throw new Error(message);
  }

  private describeBlockReason(reason?: string): string {
    if (!reason) return 'Gemini blocked the request due to safety filters';
    const upper = String(reason).toUpperCase();
    const map: Record<string, string> = {
      BLOCK_REASON_UNSPECIFIED: 'Gemini blocked the request for safety review',
      SAFETY: 'Gemini safety rules blocked the request (SAFETY)',
      PROHIBITED_CONTENT: 'Gemini blocked the request due to prohibited content (PROHIBITED_CONTENT)',
      BLOCKLIST: 'Gemini blocked the request because it matched a protected term (BLOCKLIST)',
      IMAGE_SAFETY: 'Gemini blocked the image output for safety concerns (IMAGE_SAFETY)',
      OTHER: 'Gemini blocked the request (OTHER)',
    };
    return map[upper] || `Gemini blocked the request (${upper})`;
  }

  private formatSafetyCategory(category?: string): string | undefined {
    if (!category) return undefined;
    const normalized = String(category)
      .replace(/^HARM_CATEGORY_/, '')
      .replace(/_/g, ' ')
      .toLowerCase();
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : undefined;
  }

  private async delayWithAbort(delayMs: number, signal?: AbortSignal): Promise<void> {
    if (delayMs <= 0) return;

    await new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout>;

      const handleAbort = () => {
        clearTimeout(timeoutId);
        if (signal) {
          signal.removeEventListener('abort', handleAbort);
        }
        const abortError = new Error('Request aborted');
        (abortError as any).name = 'AbortError';
        reject(abortError);
      };

      timeoutId = setTimeout(() => {
        if (signal) {
          signal.removeEventListener('abort', handleAbort);
        }
        resolve();
      }, delayMs);

      if (signal) {
        if (signal.aborted) {
          handleAbort();
          return;
        }
        signal.addEventListener('abort', handleAbort, { once: true });
      }
    });
  }
}
